import React from "react";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Standard async-data state wrapper.
 *
 * Usage:
 *   <DataState loading={loading} error={error} onRetry={fetchData} empty={items.length === 0}>
 *     {children}
 *   </DataState>
 *
 * - `loading` → shows a spinner with optional label
 * - `error`   → shows AlertTriangle + the error message + a "حدّث الصفحة" retry button
 * - `empty`   → shows a neutral "لا توجد بيانات" message
 * - otherwise renders `children`
 */
export const DataState = ({
  loading,
  error,
  empty = false,
  emptyText = "لا توجد بيانات حاليًا",
  loadingText = "جاري التحميل...",
  onRetry,
  children,
  testId = "data-state",
}) => {
  if (loading) {
    return (
      <div className="py-16 text-center" data-testid={`${testId}-loading`}>
        <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#0A4D3C]" />
        <p className="text-[#5E5C58] mt-4">{loadingText}</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-16 text-center" data-testid={`${testId}-error`}>
        <div className="w-14 h-14 mx-auto rounded-full bg-[#FBE9E7] grid place-items-center">
          <AlertTriangle className="w-7 h-7 text-[#B33939]" />
        </div>
        <h3 className="font-heading text-xl text-[#222222] mt-4">تعذّر جلب البيانات</h3>
        <p className="text-[#5E5C58] mt-2 max-w-md mx-auto leading-relaxed">{error}</p>
        {onRetry && (
          <Button
            onClick={onRetry}
            className="mt-5 rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
            data-testid={`${testId}-retry`}
          >
            <RefreshCw className="w-4 h-4 ms-2" /> إعادة المحاولة
          </Button>
        )}
      </div>
    );
  }
  if (empty) {
    return (
      <div className="py-12 text-center text-[#5E5C58]" data-testid={`${testId}-empty`}>
        {emptyText}
      </div>
    );
  }
  return children;
};

export default DataState;
